// src/components/DashboardLayout.js
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Button, IconButton, useTheme, useMediaQuery, Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

// استيراد الأيقونات اللازمة
import ChatIcon from '@mui/icons-material/Chat';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import WifiIcon from '@mui/icons-material/Wifi';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import SendIcon from '@mui/icons-material/Send';
import SchoolIcon from '@mui/icons-material/School';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';

const drawerWidth = 250;

// نفس رموز نظام التصميم المستخدمة في لوحة التحكم
const DS = {
  ink: '#0f172a',
  muted: '#64748b',
  faint: '#94a3b8',
  border: '#e9eef5',
  brand: '#0ea5e9',
  brandDark: '#0284c7',
  brandSoft: '#e6f6fe',
};

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  // اسم الحساب المسجّل دخوله (يمنع اللبس بين حسابات العملاء المتعددة)
  const [accountName, setAccountName] = useState('');
  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get('/chatbot/me/')
      .then((res) => {
        if (!mounted) return;
        setAccountName(res.data?.client?.company_name || res.data?.user?.username || '');
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isAdmin');
    navigate('/login');
  };

  // قائمة العميل
  const clientMenuItems = [
    { text: 'لوحة التحكم', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'سجل المحادثات', icon: <ChatIcon />, path: '/conversations' },
    { text: 'المواعيد والحجوزات', icon: <EventAvailableRoundedIcon />, path: '/appointments' },
    { text: 'تجربة البوت', icon: <SmartToyRoundedIcon />, path: '/playground' },
    { text: 'قوالب الرسائل', icon: <TextSnippetIcon />, path: '/templates' },
    { text: 'الحملات', icon: <CampaignIcon />, path: '/campaigns' },
    { text: 'رسائل جماعية', icon: <SendIcon />, path: '/bulk-messaging' },
    { text: 'اختبار الاتصال', icon: <WifiIcon />, path: '/test-connection' },
    { text: 'مركز التعلم', icon: <SchoolIcon />, path: '/learning-center' },
    { text: 'الإعدادات', icon: <SettingsIcon />, path: '/settings' },
  ];

  // قائمة الأدمن
  const adminMenuItems = [
    { text: 'لوحة التحكم', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: 'إدارة العملاء', icon: <PeopleIcon />, path: '/admin/clients' },
    { text: 'إضافة عميل جديد', icon: <AddIcon />, path: '/admin/clients/new' },
    { text: 'إدارة الأقسام', icon: <BusinessIcon />, path: '/admin/departments' },
    { text: 'إحصائيات التحويل', icon: <TrendingUpIcon />, path: '/admin/transfer-stats' },
    { text: 'الرسوم البيانية', icon: <AssessmentIcon />, path: '/admin/charts' },
    { text: 'سجلات النظام', icon: <HistoryIcon />, path: '/admin/logs' },
  ];

  const menuItems = isAdmin ? adminMenuItems : clientMenuItems;

  const isActive = (path) =>
    path === '/dashboard' || path === '/admin/dashboard'
      ? location.pathname === path || location.pathname === '/admin'
      : location.pathname.startsWith(path);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#ffffff' }}>
      <Toolbar />
      <Typography
        sx={{
          px: 2.5, pt: 2.5, pb: 1, color: DS.faint, fontWeight: 700,
          fontSize: '0.68rem', letterSpacing: '0.08em',
        }}
      >
        {isAdmin ? 'إدارة المنصة' : 'القائمة الرئيسية'}
      </Typography>
      <Box sx={{ overflow: 'auto', px: 1.25, flexGrow: 1 }}>
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {menuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  selected={active}
                  onClick={isMobile ? handleDrawerToggle : undefined}
                  sx={{
                    py: 1.1,
                    color: active ? DS.brandDark : DS.muted,
                    '&:hover': { bgcolor: active ? DS.brandSoft : '#f4f7fb' },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: 40, color: active ? DS.brand : DS.faint, '& svg': { fontSize: 21 } }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: active ? 700 : 600 }}
                  />
                  {active && (
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: DS.brand }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box sx={{ p: 2, borderTop: `1px solid ${DS.border}`, textAlign: 'center' }}>
        {accountName && (
          <Typography noWrap title={accountName}
            sx={{ fontWeight: 800, fontSize: '0.85rem', color: DS.ink, mb: 0.75 }}>
            {accountName}
          </Typography>
        )}
        <Chip
          label={isAdmin ? 'حساب مدير' : 'حساب عميل'}
          size="small"
          sx={{ bgcolor: DS.brandSoft, color: DS.brandDark, fontWeight: 700, width: '100%' }}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          width: '100%',
          left: 0,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          color: DS.ink,
          borderBottom: `1px solid ${DS.border}`,
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 2, sm: 3 }, width: '100%' }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 1, sm: 2 }, display: { md: 'none' }, color: DS.muted }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 42, height: 42, borderRadius: 2.5,
                background: `linear-gradient(135deg, ${DS.brand} 0%, ${DS.brandDark} 100%)`,
                display: 'grid', placeItems: 'center', flexShrink: 0,
                boxShadow: '0 4px 12px rgba(14,165,233,0.35)',
              }}
            >
              <img
                src="/meta-cloud-logo.png"
                alt="Meta Cloud"
                style={{ height: 28, width: 'auto', filter: 'brightness(0) invert(1)', objectFit: 'contain' }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 800, color: DS.ink, lineHeight: 1.2 }}
              >
                Meta Cloud
              </Typography>
              <Typography noWrap sx={{ fontSize: '0.7rem', color: accountName ? DS.brandDark : DS.faint, fontWeight: accountName ? 700 : 400 }}>
                {accountName || 'منصة المساعد الذكي للأعمال'}
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={handleLogout}
            startIcon={<LogoutRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: DS.muted,
              border: `1px solid ${DS.border}`,
              borderRadius: 2.5,
              px: { xs: 1.5, sm: 2 },
              fontSize: '0.85rem',
              '&:hover': { bgcolor: '#fff1f2', color: '#e11d48', borderColor: '#fecdd3' },
              '& .MuiButton-startIcon': { ml: 0.5, mr: -0.25 },
            }}
          >
            {isMobile ? 'خروج' : 'تسجيل الخروج'}
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderLeft: 'none' },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderLeft: 'none',
              borderRight: `1px solid ${DS.border}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: '#f4f7fb',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, pb: 2 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
